import React from "react";
import {Link} from "react-router-dom";
import {
  IS_INTEGRATIONS_FEATURE_ENABLED,
  IS_TELEGRAM_INTEGRATION_ENABLED,
} from "../../../../services/config";
import {StyledAutomationCard} from "../automations.style";
import {
  EmailIcon,
  FileDeliveryIcon,
  TelegramIcon,
  GoogleDriveIcon,
  ZapierIcon,
  FacebookPixelIcon,
} from "../icons";
import {GoogleCalenderIcon} from "../icons/google-calender-icon";
import {GoogleMeetIcon} from "../icons/google-meet-icon";
import {ZoomIcon} from "../icons/zoom-icon";
import {TiktokIcon} from "../icons/tiktok-icon";
import {useGetCoachConnectCalendarStatusContext} from "../../../../context/connect-calendar/connect-calendar-status.context";
import {useGetIntegrationsStatus} from "../../../../react-query-hooks/integrations/integrations";
import {AUTOMATION_INTEGRATION_CARD_TITLES} from "./automation-integration-card-titles";

export interface AutomationCardProps {
  title: string;
  description: string;
  comingSoon?: boolean;
  connected?: boolean;
  borderColor: string;
  icon: React.ReactNode;
  link: string;
}

export function AutomationCard({
  title,
  description,
  comingSoon,
  connected,
  borderColor,
  icon,
  link,
}: AutomationCardProps) {
  const isClickable = !comingSoon && link && link !== "#";

  const cardContent = (
    <>
      <div className="automation-card-icon-wrap">{icon}</div>
      <div className="automation-card-header">
        <h3 className="automation-card-title">{title}</h3>
        {connected && <span className="automation-card-connected">Connected</span>}
        {!connected && comingSoon && (
          <span className="automation-card-coming-soon">Coming soon</span>
        )}
      </div>
      <p className="automation-card-desc">{description}</p>
    </>
  );

  if (isClickable) {
    return (
      <Link
        className="automation-card-link"
        to={link}
        style={{textDecoration: "none", color: "inherit"}}
      >
        <StyledAutomationCard $borderColor={borderColor} $clickable>
          {cardContent}
        </StyledAutomationCard>
      </Link>
    );
  }

  return (
    <StyledAutomationCard $borderColor={borderColor}>{cardContent}</StyledAutomationCard>
  );
}

const workflowsData: Omit<AutomationCardProps, "icon">[] = [
  {
    title: "Email automations",
    description:
      "Send emails automatically after a purchase or when a purchase is abandoned",
    comingSoon: false,
    borderColor: "#D9D9D9",
    link: "/automations/email",
  },
  {
    title: "File delivery",
    description:
      "Automatically send files when a customer makes a purchase or completes an action",
    comingSoon: true,
    borderColor: "#D9D9D9",
    link: "#",
  },
];

const integrationsData: Omit<AutomationCardProps, "icon">[] = [
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.TIKTOK_PIXEL,
    description: "Track what your TikTok audience does on your profile",
    comingSoon: false,
    borderColor: "#D9D9D9",
    link: "/automations/integrations/tiktok",
  },
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.FACEBOOK_META_PIXEL,
    description: "Track your Facebook audience does on your profile",
    comingSoon: false,
    borderColor: "#D9D9D9",
    link: "/automations/integrations/facebook-pixel",
  },
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_CALENDAR,
    description: "Connect to manage your availability and keep bookings in sync",
    comingSoon: false,
    borderColor: "#D9D9D9",
    link: "/automations/integrations/google-calendar",
  },
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_MEET,
    description: "Get auto-generated links for every session and event",
    comingSoon: false,
    borderColor: "#D9D9D9",
    link: "/automations/integrations/google-meet",
  },
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.ZOOM,
    description: "Sync and manage meetings to Zoom automatically",
    comingSoon: !IS_INTEGRATIONS_FEATURE_ENABLED,
    borderColor: "#D9D9D9",
    link: IS_INTEGRATIONS_FEATURE_ENABLED ? "/automations/integrations/zoom" : "#",
  },
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.TELEGRAM,
    description:
      "Send notifications, updates, or messages directly to your Telegram account",
    comingSoon: !IS_TELEGRAM_INTEGRATION_ENABLED,
    borderColor: "#D9D9D9",
    link: IS_TELEGRAM_INTEGRATION_ENABLED ? "/automations/integrations/telegram" : "#",
  },
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_DRIVE,
    description: "Sync and manage files to Google drive automatically",
    comingSoon: true,
    borderColor: "#D9D9D9",
    link: "#",
  },
  {
    title: AUTOMATION_INTEGRATION_CARD_TITLES.ZAPIER,
    description: "Build custom automations and integrations with other apps",
    comingSoon: true,
    borderColor: "#D9D9D9",
    link: "#",
  },
];

const workflowIcons: Record<number, React.ReactNode> = {
  0: <EmailIcon />,
  1: <FileDeliveryIcon />,
};

const integrationIconsByTitle: Record<string, React.ReactNode> = {
  [AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_CALENDAR]: <GoogleCalenderIcon />,
  [AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_MEET]: <GoogleMeetIcon />,
  [AUTOMATION_INTEGRATION_CARD_TITLES.ZOOM]: <ZoomIcon />,
  [AUTOMATION_INTEGRATION_CARD_TITLES.TIKTOK_PIXEL]: <TiktokIcon />,
  [AUTOMATION_INTEGRATION_CARD_TITLES.FACEBOOK_META_PIXEL]: <FacebookPixelIcon />,
  [AUTOMATION_INTEGRATION_CARD_TITLES.TELEGRAM]: <TelegramIcon />,
  [AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_DRIVE]: <GoogleDriveIcon />,
  [AUTOMATION_INTEGRATION_CARD_TITLES.ZAPIER]: <ZapierIcon />,
};

function resolveIntegrationConnected(
  title: string,
  hasCalendarAccess: boolean,
  zoomConnected: boolean,
  tiktokConnected: boolean,
  telegramConnected: boolean,
  fallback?: boolean
): boolean | undefined {
  if (
    title === AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_CALENDAR ||
    title === AUTOMATION_INTEGRATION_CARD_TITLES.GOOGLE_MEET
  ) {
    return hasCalendarAccess;
  }
  if (title === AUTOMATION_INTEGRATION_CARD_TITLES.ZOOM) {
    return zoomConnected;
  }
  if (title === AUTOMATION_INTEGRATION_CARD_TITLES.TIKTOK_PIXEL) {
    return tiktokConnected;
  }
  if (title === AUTOMATION_INTEGRATION_CARD_TITLES.TELEGRAM) {
    return telegramConnected;
  }
  return fallback;
}

export function WorkflowsCardList() {
  return (
    <>
      {workflowsData.map((item, idx) => (
        <AutomationCard key={item.title} {...item} icon={workflowIcons[idx]} />
      ))}
    </>
  );
}

export function IntegrationsCardList() {
  const {state: connectCalendarStatus} = useGetCoachConnectCalendarStatusContext();
  const integrationsStatusQuery = useGetIntegrationsStatus();

  return (
    <>
      {integrationsData.map((item) => (
        <AutomationCard
          key={item.title}
          {...item}
          connected={resolveIntegrationConnected(
            item.title,
            connectCalendarStatus.connectCalendarStatus.has_access_token,
            Boolean(integrationsStatusQuery.data?.zoomConnected),
            Boolean(integrationsStatusQuery.data?.tiktokConnected),
            Boolean(integrationsStatusQuery.data?.telegramConnected),
            item.connected
          )}
          icon={integrationIconsByTitle[item.title]}
        />
      ))}
    </>
  );
}
